import React, { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Thumbs, EffectFade } from 'swiper/modules'
import posts from '../data/data-tour.json'
import { companyInfo, tourRecentPosts } from '../data/companyInfo'
import useRemoteModule from '../../hooks/useRemoteModule'
import { mapTourItem } from '../../services/contentApi'

function TourDetailsMain() {
    const { slug } = useParams();
    const [thumbsSwiper, setThumbsSwiper] = useState(null);
    const tourPosts = useRemoteModule('tours', mapTourItem, posts);

    const selectedTour = useMemo(() => {
        return tourPosts.find((post) => post.slug === slug) || tourPosts[0];
    }, [slug, tourPosts]);

    const days = useMemo(() => {
        return selectedTour.itinerary.map((day, index) => ({
            id: `day-tab${index + 1}`,
            label: day.label,
            items: day.items
        }));
    }, [selectedTour]);

    const [activeTab, setActiveTab] = useState(days[0]?.id || 'day-tab1');

    useEffect(() => {
        setActiveTab(days[0]?.id || 'day-tab1');
    }, [days]);

    const images = selectedTour.gallery?.length
        ? selectedTour.gallery
        : [
            '/assets/img/tour/tour_inner_1.jpg',
            '/assets/img/tour/tour_inner_2.jpg',
            '/assets/img/tour/tour_inner_3.jpg'
        ];

    return (
        <section className="space">
            <div className="container shape-mockup-wrap">
                <div className="row">
                    <div className="col-xxl-8 col-lg-7">
                        <div className="tour-page-single">
                            <div className="slider-area tour-slider1">
                                <Swiper
                                    modules={[Navigation, Thumbs, EffectFade]}
                                    effect="fade"
                                    loop={true}
                                    spaceBetween={10}
                                    navigation={{
                                        prevEl: '.slider-prev',
                                        nextEl: '.slider-next'
                                    }}
                                    thumbs={{ swiper: thumbsSwiper }}
                                    className="swiper th-slider mb-4"
                                    id="tourSlider4"
                                >
                                    {images.map((img, index) => (
                                        <SwiperSlide key={index}>
                                            <div className="tour-slider-img">
                                                <img src={img} alt={`${selectedTour.title} ${index + 1}`} />
                                            </div>
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                                <Swiper
                                    modules={[Navigation, Thumbs]}
                                    loop={true}
                                    spaceBetween={25}
                                    slidesPerView={3}
                                    watchSlidesProgress
                                    onSwiper={setThumbsSwiper}
                                    className="swiper th-slider tour-thumb-slider"
                                >
                                    {images.map((img, index) => (
                                        <SwiperSlide key={index}>
                                            <div className="tour-slider-img">
                                                <img src={img} alt={`Thumbnail ${index + 1}`} />
                                            </div>
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                                <button data-slider-prev="#tourSlider4" className="slider-arrow style3 slider-prev">
                                    <img src="/assets/img/icon/hero-arrow-left.svg" alt="" />
                                </button>
                                <button data-slider-next="#tourSlider4" className="slider-arrow style3 slider-next">
                                    <img src="/assets/img/icon/hero-arrow-right.svg" alt="" />
                                </button>
                            </div>
                            <div className="page-content">
                                <div className="page-meta mb-45">
                                    <Link className="page-tag mr-5" to="/tour">
                                        Featured
                                    </Link>
                                    <span className="ratting">
                                        <i className="fa-sharp fa-solid fa-star" />
                                        <span>4.8</span>
                                    </span>
                                </div>
                                <h2 className="box-title">{selectedTour.title}</h2>
                                <h4 className="tour-price">
                                    <span className="currency">{selectedTour.price}</span>
                                </h4>
                                <p className="box-text mb-30">{selectedTour.overviewOne}</p>
                                <p className="box-text mb-50">{selectedTour.overviewTwo}</p>

                                <h2 className="box-title">Highlights</h2>
                                <div className="checklist mb-50">
                                    <ul>
                                        {selectedTour.highlights.map((item, index) => (
                                            <li key={index}>{item}</li>
                                        ))}
                                    </ul>
                                </div>

                                <h2 className="box-title">Basic Information</h2>
                                <div className="destination-checklist mb-50">
                                    <div className="checklist style2">
                                        <ul>
                                            <li>Destination</li>
                                            <li>Departure</li>
                                            <li>Language</li>
                                            <li>Return Date</li>
                                            <li>Departure Date</li>
                                            <li>No. of Guide</li>
                                            <li>Group Size</li>
                                        </ul>
                                    </div>
                                    <div className="checklist style2">
                                        <ul>
                                            <li>{selectedTour.basicInfo.destination}</li>
                                            <li>{selectedTour.basicInfo.departure}</li>
                                            <li>{selectedTour.basicInfo.language}</li>
                                            <li>{selectedTour.basicInfo.returnDate}</li>
                                            <li>{selectedTour.basicInfo.departureDate}</li>
                                            <li>{selectedTour.basicInfo.guide}</li>
                                            <li>{selectedTour.basicInfo.groupSize}</li>
                                        </ul>
                                    </div>
                                </div>

                                <h2 className="box-title">Included and Excluded</h2>
                                <div className="destination-checklist">
                                    <div className="checklist style2 style4">
                                        <ul>
                                            {selectedTour.included.map((item, index) => (
                                                <li key={index}>{item}</li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div className="checklist style5">
                                        <ul>
                                            {selectedTour.excluded.map((item, index) => (
                                                <li key={index}>{item}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>

                                <h3 className="page-title mt-50 mb-0">Tour Plan</h3>
                                <div>
                                    <ul className="nav nav-tabs tour-tab mt-10" role="tablist">
                                        {days.map((day) => (
                                            <li className="nav-item" key={day.id} role="presentation">
                                                <button
                                                    className={`nav-link ${activeTab === day.id ? 'active' : ''}`}
                                                    onClick={() => setActiveTab(day.id)}
                                                    type="button"
                                                    role="tab"
                                                    aria-controls={`${day.id}-pane`}
                                                    aria-selected={activeTab === day.id}
                                                >
                                                    {day.label}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>

                                    <div className="tab-content">
                                        {days.map((day) => (
                                            <div
                                                key={day.id}
                                                className={`tab-pane fade ${activeTab === day.id ? 'show active' : ''}`}
                                                id={`${day.id}-pane`}
                                                role="tabpanel"
                                                aria-labelledby={day.id}
                                                tabIndex={0}
                                            >
                                                <div className="tour-grid-plan">
                                                    <div className="checklist">
                                                        <ul>
                                                            {day.items.map((item, index) => (
                                                                <li key={index}>{item}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-xxl-4 col-lg-5">
                        <aside className="sidebar-area">
                            <div className="widget widget_search">
                                <form className="search-form">
                                    <input type="text" placeholder="Search" />
                                    <button type="submit">
                                        <i className="far fa-search" />
                                    </button>
                                </form>
                            </div>
                            <div className="widget widget_categories">
                                <h3 className="widget_title">Categories</h3>
                                <ul>
                                    <li><Link to="/blog"><img src="/assets/img/theme-img/map.svg" alt="" />Mountain Tours</Link><span>(8)</span></li>
                                    <li><Link to="/blog"><img src="/assets/img/theme-img/map.svg" alt="" />Trekking & Hiking</Link><span>(6)</span></li>
                                    <li><Link to="/blog"><img src="/assets/img/theme-img/map.svg" alt="" />Camping & Outdoors</Link><span>(5)</span></li>
                                    <li><Link to="/blog"><img src="/assets/img/theme-img/map.svg" alt="" />Honeymoon Packages</Link><span>(4)</span></li>
                                    <li><Link to="/blog"><img src="/assets/img/theme-img/map.svg" alt="" />Cultural Exploration</Link><span>(3)</span></li>
                                    <li><Link to="/blog"><img src="/assets/img/theme-img/map.svg" alt="" />Customized Packages</Link><span>(9)</span></li>
                                </ul>
                            </div>
                            <div className="widget">
                                <h3 className="widget_title">Recent Posts</h3>
                                <div className="recent-post-wrap">
                                    {tourRecentPosts[selectedTour.slug]?.map((post, index) => (
                                        <div className="recent-post" key={index}>
                                            <div className="media-img"><Link to="/blog/1"><img src={post.image} alt="Blog" /></Link></div>
                                            <div className="media-body">
                                                <h4 className="post-title"><Link className="text-inherit" to="/blog/1">{post.title}</Link></h4>
                                                <div className="recent-post-meta"><Link to="/blog"><i className="fa-regular fa-calendar" />{post.date}</Link></div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="widget widget_offer" style={{ background: 'url(/assets/img/bg/widget_bg_1.jpg)' }}>
                                <div className="offer-banner">
                                    <div className="offer">
                                        <h6 className="box-title">Need Help? We Are Here To Help You</h6>
                                        <div className="banner-logo"><img src="/assets/img/primary-logo.png" alt="Himolan Peak Adventures" /></div>
                                        <div className="offer">
                                            <h6 className="offer-title">You Get Online support</h6>
                                            <Link className="offter-num" to={`tel:${companyInfo.phoneDial}`}>{companyInfo.phoneDisplay}</Link>
                                        </div>
                                        <Link to="https://wa.me/03479020168?text=Assalam%20o%20Alaikum%21%20I%20want%20to%20book%20a%20tour%20with%20Himolan%20Peak%20Adventures." className="th-btn style2 th-icon">Read More</Link>
                                    </div>
                                </div>
                            </div>
                        </aside>
                    </div>
                </div>

                <div className="location-map">
                    <h3 className="page-title mt-45 mb-30">Location</h3>
                    <div className="contact-map style3">
                        <iframe src={companyInfo.mapEmbedUrl} title="Tour Location Map" allowFullScreen="" loading="lazy" />
                        <div className="contact-icon">
                            <img src="/assets/img/icon/location-dot3.svg" alt="" />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default TourDetailsMain


